import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "../Sidenav/styles/sidenavCollapse";

import { useMaterialUIController } from "../../context";

function SidenavCollapsible({
  icon,
  name,
  children,
  active = false,
  defaultOpen = false,
  ...rest
}: any) {
  const [controller] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;

  const [open, setOpen] = useState(Boolean(defaultOpen || active));
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const collapsedRail = Boolean(isLgUp && miniSidenav);

  useEffect(() => {
    if (collapsedRail) {
      setOpen(false);
      return;
    }
    if (active) {
      setOpen(true);
    }
  }, [collapsedRail, active]);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem component="li" disableGutters sx={{ display: "block", py: 0, minWidth: 0, overflow: "hidden" }}>
        <Box
          {...rest}
          onClick={handleClick}
          sx={(theme: any) => ({
            ...collapseItem(theme, {
              active,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              sidenavColor,
              miniSidenav,
            }),
            cursor: "pointer",
          })}
        >
          <ListItemIcon
            sx={(theme) =>
              collapseIconBox(theme, {
                transparentSidenav,
                whiteSidenav,
                darkMode,
                active,
                miniSidenav,
              })
            }
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { 
                active, 
                transparentSidenav, 
                whiteSidenav, 
                darkMode 
              })}>
                {icon}
              </Icon>
            ) : (
              icon
            )}
          </ListItemIcon>

          <ListItemText
            primary={name}
            primaryTypographyProps={{ fontWeight: 400 }}
            sx={(theme) =>
              collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active,
                darkMode,
              })
            }
          />

          <Icon
            sx={{
              display: collapsedRail ? "none" : "inline-flex",
              flexShrink: 0,
              fontSize: "1.1rem !important",
              ml: 0.5,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            expand_more
          </Icon>
        </Box>
      </ListItem>
      
      <Collapse in={!collapsedRail && open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
}

// Typechecking props for the SidenavCollapsible
SidenavCollapsible.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  active: PropTypes.bool,
  defaultOpen: PropTypes.bool,
};

export default SidenavCollapsible; 